import { useState, useEffect, useCallback } from 'react';
import type { Lead, LeadFormData, Process } from '../types/lead';
import { leadService } from '../services/domain/leadService';
import type { StatusCount } from '../types/status';
import type { User } from '../types/lead';

interface UseLeadsResult {
  data: Lead[];
  users: User[];
  processes: Process[];
  leadData: Lead | null;
  statusCount: StatusCount[];
  loading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  refetch: () => void;
  updating: boolean;
  searching: boolean;
  creating: boolean;
  createLead: (formData: LeadFormData) => Promise<{ success: boolean, message?: string }>;
  searchLeadById: (id: number) => Promise<{ success: boolean, message?: string }>;
  bulkAssignLeads: (leadIds: number[], userId: number) => Promise<{ success: boolean, message?: string }>;
}

interface LeadParams {
  status_persona_id?: number;
  page?: number;
  name_persona?: string;
  pageSize?: number,
  tipo_persona_id?: string,
  searchTerm?: string;
  agentId?: string;
  sourceId?: string;
  statusId?: string;
}

export const useLead = (params: LeadParams): UseLeadsResult => {
  const [data, setData] = useState<Lead[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [leadData, setLeadData] = useState<Lead | null>(null)
  const [statusCount, setStatusCount] = useState<StatusCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [updating] = useState(false);
  const [searching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [processes, setProcesses] = useState<Process[]>([]);
  const fetchProcesses = useCallback(async () => {
    try {
      setError(null);
      const response = await leadService.getProcesses();

      // Ordenar por process_order antes de guardar en el estado
      const sortedProcesses = response.data.sort((a: any, b: any) => a.process_order - b.process_order);
      setProcesses(sortedProcesses);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLead = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Construir parámetros de búsqueda
      const searchParams: any = {
        page: params.page,
        pageSize: params.pageSize
      };

      // Agregar filtros solo si tienen valor
      if (params.searchTerm) {
        searchParams.searchTerm = params.searchTerm;
      }
      if (params.agentId) {
        searchParams.user_id = params.agentId;
      }
      if (params.sourceId) {
        searchParams.lead_source_id = params.sourceId;
      }
      if (params.statusId) {
        searchParams.lead_status_id = params.statusId;
      }
      if (params.name_persona) {
        searchParams.name_persona = params.name_persona;
      }
      if (params.status_persona_id) {
        searchParams.status_persona_id = params.status_persona_id;
      }
      if (params.tipo_persona_id) {
        searchParams.tipo_persona_id = params.tipo_persona_id;
      }

      const response = await leadService.getLeads(searchParams);
      
      setData(response.leads);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.pages);
      fetchUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    params.page, 
    params.pageSize,
    params.searchTerm,
    params.agentId,
    params.sourceId,
    params.statusId,
    params.status_persona_id, 
    params.name_persona,
    params.tipo_persona_id
  ]);

  const searchLeadById = useCallback(async (id: number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await leadService.getLeadsbyId(id);
      if(response){
        setLeadData(response);
      }
      

      return ({success: true})
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return ({success: false})
    } finally {
      setLoading(false);
    }
  }, [fetchLead]);

  useEffect(() => {
    fetchLead();
    fetchProcesses();
    fetchStatusCount();
  }, [fetchLead]);

  const refetch = useCallback(async () => {
    try {
      fetchLead();
    }
    catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en actualizar';
      setError(errorMessage);
    } finally {
      setLoading(false)
    }
  }, [fetchLead]);

  const createLead = useCallback(async (
    formData: LeadFormData
  ) => {
    try {
      setCreating(true);
      setError(null);
      const result = await leadService.createLead(formData);
      if (result.error) {
        setError(`Error al crear lead: ${result.error}`);
        return {
        success: false,
      };
      }

      await fetchLead();
      await fetchStatusCount();

      return {
        success: true,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setCreating(false);
    }
  }, [fetchLead]);

  
const fetchUsers = useCallback(async () => {
    try {
      setError(null);
      const response = await leadService.getUsers();

      setUsers(response.data.users);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching procesos:', err);
    } finally {
      setLoading(false);
    }
  }, []);
 

  const fetchStatusCount = useCallback(async () => {
    try {
      setError(null);
      const response = await leadService.getStatusCount();
      setStatusCount(response.status_counts);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fetchLead]);

  const bulkAssignLeads = useCallback(async (leadIds: number[], userId: number) => {
    try {
      // setLoading(true);
      setError(null);

      const response = await leadService.bulkAssignLeads(leadIds, userId);
      if(response){
        return ({success: true})
        // fetchLead();
      }

      return ({success: false})
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      return ({success: false})
    } finally {
      setLoading(false);
    }
  }, [fetchLead]);



  return {
    data,
    users,
    processes,
    leadData,
    statusCount,
    loading,
    error,
    total,
    totalPages,
    refetch,
    updating,
    searching,
    creating,
    createLead,
    searchLeadById,
    bulkAssignLeads
  };
};